import React, { useState, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { ChevronDown, PlayCircle, Users, Star, Award, User, Calendar, Clock, MapPin, Video, CheckCircle, Lock, BookOpen } from 'lucide-react';
import './ViewCourse.css';
import { useInstructorLearningsStore } from '@/stores/InstructorStore/InstructorLearningsStore';
import { LoadingSpinner, ErrorDisplay } from '@/Components/common';
import {
  getCourseOfferings,
  getCourseSessions,
  getStudentProgress,
  type CourseOffering,
  type CourseSession
} from '@/services/InstructorService/InstructorCourseManagementService';

// Enrolled student interface
interface EnrolledStudent {
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;
  enrolledDate: string;
  progressPercentage?: number;
  completedLessons?: number;
  totalLessonsAvailable?: number;
}

export default function ViewCoursePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { courseId } = useParams<{ courseId: string }>();
  const sourceLabel = (location.state as any)?.sourceLabel || 'My Courses';
  const [expandedSection, setExpandedSection] = useState<string | null>('introduction');
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);
  const [currentReviewPage, setCurrentReviewPage] = useState(1);
  const reviewsPerPage = 5;

  // Offerings and Sessions state
  const [offerings, setOfferings] = useState<CourseOffering[]>([]);
  const [sessions, setSessions] = useState<CourseSession[]>([]);
  const [loadingOfferings, setLoadingOfferings] = useState(false);
  const [loadingSessions, setLoadingSessions] = useState(false);

  // Enrolled Students state
  const [enrolledStudents, setEnrolledStudents] = useState<EnrolledStudent[]>([]);
  const [loadingStudents, setLoadingStudents] = useState(false);
  const [currentStudentPage, setCurrentStudentPage] = useState(1);
  const studentsPerPage = 10;

  const {
    courseDetails,
    loadingCourseDetails,
    errorCourseDetails,
    savedCourseIds,
    fetchCourseDetails,
    toggleSaveCourse,
  } = useInstructorLearningsStore();

  // Fetch course details on mount using courseId from URL params
  useEffect(() => {
    console.log('[Trainer ViewCourse] Component mounted with courseId:', courseId);
    if (courseId) {
      const id = parseInt(courseId);
      console.log('[Trainer ViewCourse] Fetching course details for ID:', id);
      fetchCourseDetails(id);

      // Fetch offerings and sessions
      loadOfferingsAndSessions(id);
    } else {
      console.error('[Trainer ViewCourse] No courseId in URL params');
    }
  }, [courseId, fetchCourseDetails]);

  // Load offerings and sessions for the course
  const loadOfferingsAndSessions = async (id: number) => {
    // Fetch offerings
    setLoadingOfferings(true);
    try {
      const offeringsResult = await getCourseOfferings(id);
      if (offeringsResult.success && offeringsResult.data) {
        setOfferings(offeringsResult.data);
      }
    } catch (error) {
      console.error('Error loading offerings:', error);
    } finally {
      setLoadingOfferings(false);
    }

    // Fetch sessions
    setLoadingSessions(true);
    try {
      const sessionsResult = await getCourseSessions(id);
      if (sessionsResult.success && sessionsResult.data) {
        setSessions(sessionsResult.data);
      }
    } catch (error) {
      console.error('Error loading sessions:', error);
    } finally {
      setLoadingSessions(false);
    }

    // Fetch enrolled students
    setLoadingStudents(true);
    try {
      const studentsResult = await getStudentProgress(id);
      if (studentsResult.success && studentsResult.data) {
        setEnrolledStudents(studentsResult.data);
      }
    } catch (error) {
      console.error('Error loading enrolled students:', error);
    } finally {
      setLoadingStudents(false);
    }
  };

  const toggleSection = (sectionId: string) => {
    setExpandedSection(expandedSection === sectionId ? null : sectionId);
  };

  const toggleFaq = (faqId: number) => {
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  // Pagination helpers for reviews
  const getPaginatedReviews = () => {
    if (!courseDetails?.reviews) return [];
    const startIndex = (currentReviewPage - 1) * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    return courseDetails.reviews.slice(startIndex, endIndex);
  };

  const getTotalReviewPages = () => {
    if (!courseDetails?.reviews) return 0;
    return Math.ceil(courseDetails.reviews.length / reviewsPerPage);
  };

  const handleNextReviewPage = () => {
    if (currentReviewPage < getTotalReviewPages()) {
      setCurrentReviewPage(currentReviewPage + 1);
    }
  };

  const handlePreviousReviewPage = () => {
    if (currentReviewPage > 1) {
      setCurrentReviewPage(currentReviewPage - 1);
    }
  };

  const handleReviewPageClick = (pageNumber: number) => {
    setCurrentReviewPage(pageNumber);
  };

  // Pagination helpers for enrolled students
  const getPaginatedStudents = () => {
    if (!enrolledStudents) return [];
    const startIndex = (currentStudentPage - 1) * studentsPerPage;
    const endIndex = startIndex + studentsPerPage;
    return enrolledStudents.slice(startIndex, endIndex);
  };

  const getTotalStudentPages = () => {
    if (!enrolledStudents) return 0;
    return Math.ceil(enrolledStudents.length / studentsPerPage);
  };

  const handleNextStudentPage = () => {
    if (currentStudentPage < getTotalStudentPages()) {
      setCurrentStudentPage(currentStudentPage + 1);
    }
  };

  const handlePreviousStudentPage = () => {
    if (currentStudentPage > 1) {
      setCurrentStudentPage(currentStudentPage - 1);
    }
  };

  const handleStudentPageClick = (pageNumber: number) => {
    setCurrentStudentPage(pageNumber);
  };

  // Add handlers for wishlist and enroll buttons
  const handleToggleSave = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (courseId) {
      const id = parseInt(courseId, 10);
      if (!isNaN(id)) {
        try {
          await toggleSaveCourse(id);
        } catch (error: any) {
          console.error('Failed to toggle save:', error);
        }
      }
    }
  };

  // Get lesson button text based on lesson type and completion status
  const getLessonButtonText = (lesson: any) => {
    if (lesson.lessonType === 'ILTS') {
      if (lesson.iltsMode === 'Online') {
        return lesson.isCompleted ? 'Watch Recording' : 'Join Session';
      }
      return 'View Details';
    }
    return lesson.isCompleted ? 'View Again' : 'Start Now';
  };

  // Handle lesson click - navigate to lesson viewer
  const handleLessonClick = (lesson: any) => {
    if (!lesson.isUnlocked) {
      return; // Locked lessons cannot be accessed
    }

    navigate(`/trainer/course/${courseId}/lesson/${lesson.id}`, {
      state: {
        lesson,
        courseId: parseInt(courseId!),
        courseTitle: courseDetails?.title,
      },
    });
  };

  // Loading state
  if (loadingCourseDetails) {
    console.log('[Trainer ViewCourse] Loading course details...');
    return (
      <LoadingSpinner
        fullScreen
        size="lg"
      />
    );
  }

  // Invalid courseId state
  if (!courseId || isNaN(parseInt(courseId))) {
    return (
      <ErrorDisplay
        fullScreen
        title="Invalid Course"
        message="The course ID in the URL is invalid."
        type="error"
      />
    );
  }

  // Error state
  if (errorCourseDetails) {
    console.error('[Trainer ViewCourse] Error loading course details:', errorCourseDetails);
    return (
      <ErrorDisplay
        fullScreen
        title="Failed to Load Course Details"
        message={errorCourseDetails}
        type="error"
        showRetry
        onRetry={() => fetchCourseDetails(parseInt(courseId))}
      />
    );
  }

  // No data state
  if (!courseDetails) {
    console.warn('[Trainer ViewCourse] No course details data available');
    return (
      <ErrorDisplay
        fullScreen
        title="Course Not Found"
        message="The course you're looking for doesn't exist."
        type="error"
      />
    );
  }

  console.log('[Trainer ViewCourse] Rendering course page with data:', courseDetails);

  return (
    <div className="view-course-container">
      {/* Header */}
      <header className="view-course-header">
        <div className="view-course-header__content">
          <div className="view-course-breadcrumb">
            <span
              className="view-course-breadcrumb-item view-course-breadcrumb-clickable"
              onClick={() => {
                // Navigate based on the source label
                if (sourceLabel.toLowerCase().includes('course management')) {
                  navigate('/trainer/content/course-management');
                } else if (sourceLabel.toLowerCase().includes('browse')) {
                  navigate('/trainer/my-learnings/browse-courses');
                } else if (sourceLabel.toLowerCase().includes('wishlist')) {
                  navigate('/trainer/my-learnings/wishlist');
                } else {
                  navigate('/trainer/my-learnings/my-courses');
                }
              }}
              style={{ cursor: 'pointer' }}
            >
              {sourceLabel}
            </span>
            <span className="view-course-breadcrumb-separator"> › </span>
            <span className="view-course-breadcrumb-item view-course-breadcrumb-active">
              {courseDetails?.title || 'Course Details'}
            </span>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="view-course-hero">
        <div className="view-course-hero__content">
          <div className="view-course-hero__text">
            <h1 className="view-course-hero__title">{courseDetails.title}</h1>
            <p className="view-course-hero__subtitle">
              {courseDetails.subtitle}
            </p>
            <div className="view-course-hero__stats">
              <div className="view-course-hero__stat">
                <Star size={16} color="#f59e0b" fill="#f59e0b" />
                <span className="view-course-hero__stat-text">{courseDetails.rating} ({courseDetails.totalRatings} Ratings)</span>
              </div>
              <div className="view-course-hero__stat">
                <Users size={16} color="#8b5cf6" />
                <span className="view-course-hero__stat-text">{enrolledStudents.length.toLocaleString()} Students</span>
              </div>
            </div>
            <div className="view-course-hero__instructor">
              <div className="view-course-hero__instructor-avatar">
                {courseDetails.instructor.image ? (
                  <img
                    src={courseDetails.instructor.image}
                    alt={courseDetails.instructor.name}
                    className="view-course-hero__instructor-avatar-img"
                  />
                ) : courseDetails.instructor.avatar ? (
                  <span>{courseDetails.instructor.avatar}</span>
                ) : (
                  <User size={20} />
                )}
              </div>
              <span className="view-course-hero__instructor-name">{courseDetails.instructor.name}</span>
            </div>
          </div>
          <div className="view-course-hero__image">
            <div className="view-course-hero__image-placeholder">
              {courseDetails.thumbnail ? (
                <img
                  src={courseDetails.thumbnail}
                  alt={courseDetails.title}
                  className="view-course-hero__thumbnail-img"
                  onError={(e) => {
                    // Fallback to placeholder if image fails to load
                    e.currentTarget.style.display = 'none';
                    const placeholder = e.currentTarget.nextElementSibling as HTMLElement;
                    if (placeholder) placeholder.style.display = 'flex';
                  }}
                />
              ) : null}
              <div
                className="view-course-hero__mock-image"
                style={{
                  display: courseDetails.thumbnail ? 'none' : 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  flexDirection: 'column',
                  gap: '12px'
                }}
              >
                <BookOpen size={64} color="#8b5cf6" strokeWidth={1.5} />
                <span style={{ color: '#6b7280', fontSize: '14px' }}>Course Thumbnail</span>
              </div>
            </div>
            <button
              className="view-course-hero__wishlist-btn"
              onClick={handleToggleSave}
              title={
                savedCourseIds.has(parseInt(courseId || "0"))
                  ? "Remove from wishlist"
                  : "Add to wishlist"
              }
            >
              {savedCourseIds.has(parseInt(courseId || "0"))
                ? "✓ Saved"
                : "Add to Wishlist"}
            </button>
          </div>
        </div>
      </section>

      {/* Course Info Cards */}
      <div className="view-course-info-cards">
        <div className="view-course-info-card">
          <h3 className="view-course-info-card__title">This course includes:</h3>
          <ul className="view-course-info-list">
            {courseDetails.courseIncludes.map((item, index) => (
              <li key={index} className="view-course-info-item">
                {index < 3 ? <PlayCircle size={16} color="#8b5cf6" /> : <Award size={16} color="#8b5cf6" />}
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Main Content */}
      <main className="view-course-main">
        {/* What You'll Learn */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">What you'll learn</h2>
          <div className="view-course-learning-grid">
            {courseDetails.learningPoints.map((point, index) => (
              <div key={index} className="view-course-learning-item">
                <span className="view-course-learning-checkmark">✓</span>
                <span className="view-course-learning-text">{point}</span>
              </div>
            ))}
          </div>
        </section>

        {/* Course Content */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">Course content</h2>
          <p className="view-course-content-stats">
            {courseDetails.contentStats.sections} Sections • {courseDetails.contentStats.lectures} Lectures • {courseDetails.contentStats.totalDuration} total length
          </p>
          <div className="view-course-modules">
            {courseDetails.courseModules.map((module) => (
              <div key={module.id} className="view-course-module">
                <div className="view-course-module__header" onClick={() => toggleSection(module.id)}>
                  <div className="view-course-module__header-left">
                    <ChevronDown
                      size={20}
                      style={{
                        transform: expandedSection === module.id ? 'rotate(0deg)' : 'rotate(-90deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                    <span className="view-course-module__title">{module.title}</span>
                  </div>
                  <div className="view-course-module__stats">
                    <span>{module.lectures} Lectures</span>
                    <span>{module.duration}</span>
                  </div>
                </div>
                {expandedSection === module.id && module.lessons.length > 0 && (
                  <div className="view-course-module__content">
                    {module.lessons.map((lesson, index) => (
                      <div
                        key={index}
                        className={`view-course-video-item ${
                          lesson.isCompleted ? 'completed' : ''
                        } ${!lesson.isUnlocked ? 'locked' : ''}`}
                        onClick={() => handleLessonClick(lesson)}
                        style={{
                          cursor: lesson.isUnlocked ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <div className="view-course-video-item__left">
                          {lesson.isCompleted ? (
                            <CheckCircle size={16} color="#10b981" />
                          ) : !lesson.isUnlocked ? (
                            <Lock size={16} color="#9ca3af" />
                          ) : (
                            <PlayCircle size={16} color="#6b7280" />
                          )}
                          <span className="view-course-video-item__title">
                            {lesson.title}
                          </span>
                        </div>
                        <div className="view-course-video-item__right">
                          <span className="view-course-video-item__duration">
                            {lesson.duration}
                          </span>
                          {lesson.isUnlocked && (
                            <button
                              className="view-course-video-item__action"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleLessonClick(lesson);
                              }}
                            >
                              {getLessonButtonText(lesson)}
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Offerings and Sessions - Only show if there are any */}
        {(offerings.length > 0 || sessions.length > 0) && (
          <section className="view-course-section">
            <h2 className="view-course-section__title">
              <Calendar size={24} color="#8b5cf6" />
              Training Schedules
            </h2>

            {/* Offerings */}
            {offerings.length > 0 && (
              <div style={{ marginBottom: '30px' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#1f2937' }}>
                  Course Offerings
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {offerings.map((offering) => (
                    <div
                      key={offering.id}
                      style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '12px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                          {offering.name}
                        </h4>
                        <span style={{
                          padding: '4px 12px',
                          borderRadius: '12px',
                          fontSize: '12px',
                          fontWeight: '500',
                          backgroundColor: offering.status === 'Published' ? '#dcfce7' : '#f3f4f6',
                          color: offering.status === 'Published' ? '#166534' : '#6b7280'
                        }}>
                          {offering.status}
                        </span>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '14px', color: '#6b7280' }}>
                        {offering.locationName && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <MapPin size={16} color="#8b5cf6" />
                            <span>{offering.locationName}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Calendar size={16} color="#8b5cf6" />
                          <span>{offering.startDate} - {offering.endDate}</span>
                        </div>
                        {offering.timezone && (
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <Clock size={16} color="#8b5cf6" />
                            <span>Timezone: {offering.timezone}</span>
                          </div>
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Users size={16} color="#8b5cf6" />
                          <span>
                            {offering.availableSeats !== undefined
                              ? `${offering.availableSeats}/${offering.totalSeats} seats available`
                              : `${offering.totalSeats} total seats`}
                          </span>
                        </div>
                      </div>

                      {offering.enableWaitlist && (
                        <div style={{ marginTop: '12px', fontSize: '13px', color: '#f59e0b', fontWeight: '500' }}>
                          ✓ Waitlist enabled
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Sessions */}
            {sessions.length > 0 && (
              <div>
                <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '15px', color: '#1f2937' }}>
                  Training Sessions
                </h3>
                <div style={{ display: 'grid', gap: '16px' }}>
                  {sessions.map((session) => (
                    <div
                      key={session.id}
                      style={{
                        backgroundColor: '#fff',
                        border: '1px solid #e5e7eb',
                        borderRadius: '8px',
                        padding: '20px',
                        boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                      }}
                    >
                      <div style={{ marginBottom: '12px' }}>
                        <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                          {session.name}
                        </h4>
                      </div>

                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px', fontSize: '14px', color: '#6b7280' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Calendar size={16} color="#8b5cf6" />
                          <span>{session.sessionDate}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <Clock size={16} color="#8b5cf6" />
                          <span>{session.startTime} - {session.endTime}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          {session.deliveryMethod === 'Virtual' ? (
                            <Video size={16} color="#8b5cf6" />
                          ) : (
                            <MapPin size={16} color="#8b5cf6" />
                          )}
                          <span>{session.deliveryMethod}</span>
                        </div>
                      </div>

                      {session.virtualMeetingLink && session.deliveryMethod === 'Virtual' && (
                        <div style={{ marginTop: '12px' }}>
                          <a
                            href={session.virtualMeetingLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '6px',
                              color: '#8b5cf6',
                              fontSize: '14px',
                              fontWeight: '500',
                              textDecoration: 'none'
                            }}
                          >
                            <Video size={14} />
                            Join Virtual Meeting
                          </a>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {loadingOfferings && loadingSessions && (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
                Loading training schedules...
              </div>
            )}
          </section>
        )}

        {/* Instructor */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">Instructor</h2>
          <div className="view-course-instructor-card">
            <h3 className="view-course-instructor-card__name">{courseDetails.instructor.name}</h3>
            <p className="view-course-instructor-card__title">{courseDetails.instructor.title}</p>
            <div className="view-course-instructor-card__avatar">
              {courseDetails.instructor.image ? (
                <img
                  src={courseDetails.instructor.image}
                  alt={courseDetails.instructor.name}
                  className="view-course-instructor-card__avatar-img"
                />
              ) : courseDetails.instructor.avatar ? (
                <span>{courseDetails.instructor.avatar}</span>
              ) : (
                <User size={28} />
              )}
            </div>
            <div className="view-course-instructor-card__stats">
              <div className="view-course-instructor-card__stat-item">
                <Star size={16} color="#f59e0b" fill="#f59e0b" />
                <span>{courseDetails.instructor.rating} ({courseDetails.instructor.totalRatings.toLocaleString()} Ratings)</span>
              </div>
              <div className="view-course-instructor-card__stat-item">
                <Users size={16} color="#6b7280" />
                <span>{courseDetails.instructor.totalStudents.toLocaleString()} Students</span>
              </div>
              <div className="view-course-instructor-card__stat-item">
                <PlayCircle size={16} color="#6b7280" />
                <span>{courseDetails.instructor.totalCourses} Courses</span>
              </div>
            </div>
            {courseDetails.instructor.bio.map((paragraph, index) => (
              <p key={index} className="view-course-instructor-card__bio">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        {/* Reviews */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">
            <Star size={24} color="#f59e0b" fill="#f59e0b" />
            {courseDetails.rating} Course Ratings ({courseDetails.totalRatings} Ratings)
          </h2>
          <div className="view-course-reviews">
            {getPaginatedReviews().map((review) => (
              <div key={review.id} className="view-course-review">
                <div className="view-course-review__header">
                  <div className="view-course-review__avatar">{review.avatar}</div>
                  <div className="view-course-review__header-text">
                    <span className="view-course-review__name">{review.name}</span>
                    <span className="view-course-review__time">{review.time}</span>
                  </div>
                </div>
                <div className="view-course-review__stars">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Star key={star} size={14} color="#f59e0b" fill={star <= review.rating ? "#f59e0b" : "none"} />
                  ))}
                </div>
                <p className="view-course-review__text">
                  {review.comment}
                </p>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {getTotalReviewPages() > 1 && (
            <div className="view-course-reviews-pagination">
              <button
                className="view-course-reviews-pagination__btn"
                onClick={handlePreviousReviewPage}
                disabled={currentReviewPage === 1}
              >
                Previous
              </button>
              <div className="view-course-reviews-pagination__pages">
                {Array.from({ length: getTotalReviewPages() }, (_, i) => i + 1).map((pageNum) => (
                  <button
                    key={pageNum}
                    className={`view-course-reviews-pagination__page ${
                      currentReviewPage === pageNum ? 'active' : ''
                    }`}
                    onClick={() => handleReviewPageClick(pageNum)}
                  >
                    {pageNum}
                  </button>
                ))}
              </div>
              <button
                className="view-course-reviews-pagination__btn"
                onClick={handleNextReviewPage}
                disabled={currentReviewPage === getTotalReviewPages()}
              >
                Next
              </button>
            </div>
          )}
        </section>

        {/* Enrolled Students */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">
            <Users size={24} color="#8b5cf6" />
            Enrolled Students ({enrolledStudents.length})
          </h2>

          {loadingStudents ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              Loading enrolled students...
            </div>
          ) : enrolledStudents.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px', color: '#6b7280' }}>
              No students enrolled yet.
            </div>
          ) : (
            <>
              <div className="view-course-enrolled-students">
                <table className="view-course-enrolled-students-table">
                  <thead>
                    <tr>
                      <th>Student Name</th>
                      <th>Email</th>
                      <th>Enrolled Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {getPaginatedStudents().map((student) => (
                      <tr key={student.userId}>
                        <td>
                          <div className="view-course-enrolled-student-info">
                            <div className="view-course-enrolled-student-avatar">
                              {student.profileImage ? (
                                <img src={student.profileImage} alt={`${student.firstName} ${student.lastName}`} />
                              ) : (
                                <User size={20} />
                              )}
                            </div>
                            <span>{student.firstName} {student.lastName}</span>
                          </div>
                        </td>
                        <td>{student.email}</td>
                        <td>{new Date(student.enrolledDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination Controls */}
              {getTotalStudentPages() > 1 && (
                <div className="view-course-students-pagination">
                  <button
                    className="view-course-students-pagination__btn"
                    onClick={handlePreviousStudentPage}
                    disabled={currentStudentPage === 1}
                  >
                    Previous
                  </button>
                  <div className="view-course-students-pagination__pages">
                    {Array.from({ length: getTotalStudentPages() }, (_, i) => i + 1).map((pageNum) => (
                      <button
                        key={pageNum}
                        className={`view-course-students-pagination__page ${
                          currentStudentPage === pageNum ? 'active' : ''
                        }`}
                        onClick={() => handleStudentPageClick(pageNum)}
                      >
                        {pageNum}
                      </button>
                    ))}
                  </div>
                  <button
                    className="view-course-students-pagination__btn"
                    onClick={handleNextStudentPage}
                    disabled={currentStudentPage === getTotalStudentPages()}
                  >
                    Next
                  </button>
                </div>
              )}
            </>
          )}
        </section>

        {/* FAQ */}
        <section className="view-course-section">
          <h2 className="view-course-section__title">FAQ</h2>
          <div className="view-course-faq-list">
            {courseDetails.faqs.map((faq) => (
              <div key={faq.id} className="view-course-faq-item">
                <div
                  className={`view-course-faq-question ${
                    faq.id === 2 ? 'view-course-faq-question--highlight' : ''
                  }`}
                  onClick={() => toggleFaq(faq.id)}
                >
                  <span className="view-course-faq-number">Q{faq.id}</span>
                  <span className="view-course-faq-question-text">{faq.question}</span>
                  <ChevronDown
                    size={20}
                    style={{
                      transform: expandedFaq === faq.id ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                    }}
                  />
                </div>
                {expandedFaq === faq.id && <div className="view-course-faq-answer">{faq.answer}</div>}
              </div>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
}
